import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Search } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable place autocomplete (Google-backed, server-proxied). On select it
// resolves coordinates + exact timezone and returns
// { name, lat, lon, tz_offset_min, tz_id } via onSelect.
export default function PremiumPlaceInput({ value, onSelect, placeholder = "Search city…", testid = "premium-place", biasLat = null, biasLon = null }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const bias = (biasLat != null && biasLon != null) ? `&lat=${biasLat}&lon=${biasLon}` : "";
        const res = await fetch(`${API}/api/geo/search?q=${encodeURIComponent(query)}${bias}`);
        const json = res.ok ? await res.json() : [];
        setResults(Array.isArray(json) ? json : []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, open, biasLat, biasLon]);

  const choose = async (r) => {
    setLoading(true);
    try {
      let lat = r.lat, lon = r.lon, name = r.name || (r.label || "").split(",")[0], place_id = r.place_id || "";
      if (lat == null && r.place_id) {
        const j = await (await fetch(`${API}/api/geo/place?place_id=${encodeURIComponent(r.place_id)}`)).json();
        lat = j.lat; lon = j.lon; name = j.name || name; place_id = j.place_id || place_id;
      }
      let tz_offset_min = 330, tz_id = "Asia/Kolkata", tz_name = "";
      try {
        const tj = await (await fetch(`${API}/api/geo/timezone?lat=${lat}&lon=${lon}`)).json();
        if (typeof tj.tz === "number") { tz_offset_min = tj.tz; tz_id = tj.tz_id || tz_id; tz_name = tj.tz_name || ""; }
      } catch { /* default IST */ }
      setQ(name); setOpen(false); setResults([]);
      onSelect({ name, lat: parseFloat(lat), lon: parseFloat(lon), place_id, tz_offset_min, tz_id, tz_name });
    } finally { setLoading(false); }
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <div className="flex items-center gap-2 border border-line bg-parchment px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-ink-3" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder={placeholder} data-testid={`${testid}-input`}
          className="w-full bg-transparent text-sm text-ink placeholder:text-ink-3 focus:outline-none" />
        {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gold" />}
      </div>
      {open && q.trim().length >= 2 && (
        <ul className="absolute left-0 top-full z-50 mt-1 max-h-60 w-full overflow-y-auto border border-line bg-surface shadow-lg">
          {results.length === 0 ? (
            <li className="px-3 py-2.5 text-xs text-ink-3">{loading ? "Searching…" : "No matches."}</li>
          ) : results.map((r) => (
            <li key={r.place_id || `${r.lat},${r.lon}`}>
              <button type="button" onClick={() => choose(r)} data-testid={`${testid}-option`}
                className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-ink-2 hover:bg-[var(--surface-alt)] hover:text-ink">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> <span>{r.label || r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
